import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";

const submissionResultSchema = z.object({
  draftSalesOrderId: z.string().uuid(),
  salesOrderId: z.string().uuid().nullable(),
  success: z.boolean(),
  error: z.string().optional(),
});

/**
 * Submit Draft Sales Orders Step (workflow-native approval flow).
 *
 * The terminal step of the approval canvas: flips drafts to APPROVED and,
 * depending on `erpSubmissionApi`, submits them to the configured ERP or
 * stops as a terminal "approved, no ERP submission" state. Runs after the
 * upstream team Approval steps have gated approval, so it performs no gate
 * re-validation and has no auto-submit toggle — reaching this step IS the
 * go-ahead. Idempotent per draft.
 *
 * Backend-only (DB + ERP access); handler in
 * backend/src/workflows/steps/order-management/.
 */
export const submitDraftSalesOrdersStep = defineStepConfig({
  type: "submit_draft_sales_orders",
  name: "Submit Draft Sales Orders",
  description:
    "Approves draft sales orders and optionally submits them to the configured ERP API",
  category: "Data Access",
  libraryModule: "OrderManagement",
  inputSchema: z.object({
    draftSalesOrderIds: z
      .array(z.string().uuid())
      .describe("UUIDs of the approved draft sales orders to submit"),
  }),

  outputSchema: z.object({
    results: z
      .array(submissionResultSchema)
      .describe("Per-draft submission result"),
    salesOrderIds: z
      .array(z.string().uuid())
      .describe("Ids of the sales orders created (or already existing)"),
    submittedDraftSalesOrderIds: z
      .array(z.string().uuid())
      .describe(
        "Ids of the drafts successfully submitted (all paths). For registered-API submissions this is the only per-draft identifier — no SalesOrder row is created",
      ),
    successCount: z.number().int(),
    failureCount: z.number().int(),
  }),

  configSchema: z.object({
    continueOnError: z
      .boolean()
      .default(true)
      .describe(
        "When true, a failed submission is recorded and the remaining drafts still process; when false, the step stops at the first failure",
      ),
    /**
     * Which ERP submission API to send the approved order to.
     * - undefined (legacy canvases saved before this option existed): "auto" —
     *   submit via the organization's default ERP adapter.
     * - "auto": submit via the organization's default ERP adapter.
     * - "none": approve without any ERP call — terminal "approved, no ERP
     *   submission" state (a skipped submission record is written).
     * - "builtin:oracle-ebs" / "builtin:oracle-fusion": the code adapters,
     *   selected explicitly.
     * - anything else: an ExternalApiDefinition id (schema-mapped submission
     *   API — later phase).
     */
    erpSubmissionApi: z
      .string()
      .optional()
      .describe(
        "ERP submission API: 'auto' (org default adapter), 'none' (approve without ERP), 'builtin:<erp>' or a registered submission API id",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "10 minutes",
    retry: {
      maximumAttempts: 2,
    },
    nonRetryableErrorTypes: ["ValidationError", "SchemaValidationError"],
  },
});
